// Client-side mirror of backend/src/utils/procurementTracker.js, used to show
// an inline tracker edit immediately while the save is in flight. The server's
// rows replace these as soon as the PATCH returns.
//
// Item-level fields (status, deliveredDate, freightTracking) change one row and
// fall back to the PO's value when cleared. PO-level fields (estimated delivery,
// confirmed ship) change every row on that PO.

export const TRACKER_PO_FIELDS = ['estimatedDeliveryDate', 'confirmedShipDate'];

function patchItemRow(row, changes) {
  const next = { ...row };
  if ('status' in changes) {
    const status = String(changes.status || '').trim();
    next.orderStatus = status || row.poStatus || 'No status';
    next.statusSource = status ? 'item' : 'po';
  }
  if ('deliveredDate' in changes) {
    const date = changes.deliveredDate || '';
    next.actualDeliveryDate = date || row.poActualDeliveryDate || null;
    next.actualDeliverySource = date ? 'item' : 'po';
  }
  if ('freightTracking' in changes) {
    const tracking = String(changes.freightTracking || '').trim();
    next.trackingUrl = tracking || row.poTrackingUrl || '';
    next.trackingSource = tracking ? 'item' : 'po';
  }
  return next;
}

export function applyTrackerEdit(rows, target, changes) {
  const poFields = TRACKER_PO_FIELDS.filter((field) => field in changes);

  return rows.map((row) => {
    let next = row;
    if (row.id === target.id) next = patchItemRow(row, changes);
    if (poFields.length && row.poId === target.poId) {
      next = { ...next };
      poFields.forEach((field) => {
        next[field] = changes[field] || null;
      });
    }
    return next;
  });
}

// The stored values an edit is about to overwrite, in the same shape as the
// edit — applying these undoes a failed save without touching other rows'
// edits that may have landed in the meantime.
export function previousTrackerValues(row, changes) {
  const previous = {};
  if ('status' in changes) {
    previous.status = row.statusSource === 'item' ? row.orderStatus : '';
  }
  if ('deliveredDate' in changes) {
    previous.deliveredDate = row.actualDeliverySource === 'item' ? row.actualDeliveryDate || '' : '';
  }
  if ('freightTracking' in changes) {
    previous.freightTracking = row.trackingSource === 'item' ? row.trackingUrl || '' : '';
  }
  TRACKER_PO_FIELDS.forEach((field) => {
    if (field in changes) previous[field] = row[field] || '';
  });
  return previous;
}

// Swaps in the server's fresh rows for one PO, keeping the row order stable and
// the project name (which the PATCH response doesn't look up).
export function replacePurchaseOrderRows(rows, poId, serverRows) {
  const byId = new Map((serverRows || []).map((row) => [row.id, row]));
  return rows.map((row) => {
    if (row.poId !== poId) return row;
    const fresh = byId.get(row.id);
    return fresh ? { ...fresh, projectName: row.projectName } : row;
  });
}

// The per-item status picker. "Draft" is left out because it's the placeholder
// every new PO line starts with, which the tracker treats as "same as PO" — the
// empty value in this list. A stored status that isn't configured (a custom
// status since removed) stays selectable so the <select> never renders blank.
export function itemStatusChoices(builtInStatuses, customStatuses, currentItemStatus) {
  const seen = new Set();
  const options = [];
  const add = (raw) => {
    const label = String(raw ?? '').trim();
    const key = label.toLowerCase();
    if (!label || key === 'draft' || key === 'no status' || seen.has(key)) return;
    seen.add(key);
    options.push(label);
  };
  (builtInStatuses || []).forEach(add);
  (customStatuses || []).forEach(add);
  add(currentItemStatus);
  return options;
}
